import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import API, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ArrowLeft, Loader2, Save, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function AdminArticleEditor() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isNew = !id || id === "nuovo";
    const [categories, setCategories] = useState([]);
    const [form, setForm] = useState({ title: "", category_id: "", excerpt: "", body: "", cover_image_url: "", slug: "" });
    const [status, setStatus] = useState("draft");
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [error, setError] = useState("");
    const [currentId, setCurrentId] = useState(isNew ? null : id);

    useEffect(() => { API.get("/categories").then(({ data }) => setCategories(data)).catch(() => {}); }, []);
    useEffect(() => {
        if (isNew) return;
        API.get(`/admin/articles/${id}`).then(({ data }) => {
            setForm({
                title: data.title, category_id: data.category_id,
                excerpt: data.excerpt || "", body: data.body,
                cover_image_url: data.cover_image_url || "", slug: data.slug,
            });
            setStatus(data.status);
        }).catch(e => setError(apiError(e))).finally(() => setLoading(false));
    }, [id, isNew]);

    const missing = [];
    if (form.title.length < 5) missing.push(`titolo min 5 (${form.title.length}/5)`);
    if (!form.category_id) missing.push("scegli una categoria");
    if (form.body.length < 20) missing.push(`contenuto min 20 (${form.body.length}/20)`);
    const canSave = missing.length === 0 && !saving;

    const save = async () => {
        setSaving(true);
        try {
            if (isNew && !currentId) {
                const { data } = await API.post("/admin/articles", form);
                setCurrentId(data.id);
                toast.success("Bozza salvata");
                navigate(`/admin/articoli/${data.id}`, { replace: true });
            } else {
                await API.patch(`/admin/articles/${currentId}`, form);
                toast.success("Articolo aggiornato");
            }
        } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
    };

    const togglePublish = async () => {
        if (!currentId) { toast.error("Salva prima come bozza"); return; }
        setPublishing(true);
        try {
            const url = status === "published" ? `/admin/articles/${currentId}/unpublish` : `/admin/articles/${currentId}/publish`;
            const { data } = await API.post(url);
            setStatus(data.status);
            toast.success(status === "published" ? "Articolo depubblicato" : "Articolo pubblicato");
        } catch (e) { toast.error(apiError(e)); } finally { setPublishing(false); }
    };

    if (loading) return <div className="p-16 text-center"><Loader2 className="size-6 mx-auto animate-spin" /></div>;

    return (
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-10">
            <Button asChild variant="ghost" size="sm" className="-ml-2 mb-2 text-muted-foreground">
                <Link to="/admin/articoli"><ArrowLeft className="size-4 mr-1" /> Articoli</Link>
            </Button>
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-xs uppercase tracking-widest text-primary font-semibold inline-flex items-center gap-1.5">
                        <ShieldCheck className="size-3.5" /> Editor articolo
                    </div>
                    <h1 className="mt-1 font-serif text-3xl font-semibold" data-testid="art-editor-title">{isNew ? "Nuovo articolo" : "Modifica articolo"}</h1>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full border font-semibold ${status === "published" ? "bg-emerald-600 text-white border-emerald-600" : "bg-slate-100 text-slate-700 border-slate-200"}`}>
                    {status === "published" ? "Pubblicato" : "Bozza"}
                </span>
            </div>

            {error && <Alert variant="destructive" className="mt-4"><AlertDescription>{error}</AlertDescription></Alert>}

            <Card className="mt-6 border-slate-200">
                <CardContent className="pt-6 space-y-5">
                    <div className="space-y-2">
                        <Label>Titolo * <span className="text-xs text-muted-foreground">({form.title.length}/140)</span></Label>
                        <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} maxLength={140} data-testid="art-title" />
                    </div>
                    <div className="grid gap-3 md:grid-cols-2">
                        <div className="space-y-2">
                            <Label>Categoria *</Label>
                            <Select value={form.category_id} onValueChange={v => setForm({ ...form, category_id: v })}>
                                <SelectTrigger data-testid="art-category"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                                <SelectContent>
                                    {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Slug URL (opzionale) <span className="text-xs text-muted-foreground">(auto se vuoto)</span></Label>
                            <Input value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} maxLength={100} placeholder="assemblee-guida-pratica" data-testid="art-slug" />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label>Immagine di copertina URL (opzionale)</Label>
                        <Input value={form.cover_image_url} onChange={e => setForm({ ...form, cover_image_url: e.target.value })} placeholder="https://…" data-testid="art-cover" />
                    </div>
                    <div className="space-y-2">
                        <Label>Estratto <span className="text-xs text-muted-foreground">({form.excerpt.length}/300)</span></Label>
                        <Textarea value={form.excerpt} onChange={e => setForm({ ...form, excerpt: e.target.value })} rows={2} maxLength={300} data-testid="art-excerpt" placeholder="Riassunto breve mostrato in anteprima" />
                    </div>
                    <div className="space-y-2">
                        <Label>Contenuto * <span className="text-xs text-muted-foreground">({form.body.length}/20000)</span></Label>
                        <Textarea value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} rows={16} maxLength={20000} data-testid="art-body" placeholder="Testo dell'articolo (le nuove righe vengono preservate)…" />
                    </div>
                    {missing.length > 0 && (
                        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                            Per salvare: {missing.join(" · ")}.
                        </p>
                    )}
                    <div className="flex justify-end gap-2 pt-3 border-t">
                        <Button onClick={save} disabled={!canSave} data-testid="art-save">
                            {saving ? <Loader2 className="size-4 mr-2 animate-spin" /> : <Save className="size-4 mr-2" />}
                            {isNew && !currentId ? "Salva bozza" : "Aggiorna"}
                        </Button>
                        <Button variant="outline" onClick={togglePublish} disabled={publishing || !currentId} data-testid="art-toggle-pub">
                            {publishing ? <Loader2 className="size-4 mr-2 animate-spin" /> : (status === "published" ? <EyeOff className="size-4 mr-2" /> : <Eye className="size-4 mr-2" />)}
                            {status === "published" ? "Depubblica" : "Pubblica"}
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
